/**
 * Intro Star Hero — front-end behaviour.
 *
 * Two independent, performance-minded enhancements. Ambient video: the hosted
 * MP4 autoplays muted in a loop behind the poster, a loader shows while it
 * buffers, a control lets the user pause/resume, and playback stops while the
 * block is scrolled out of view. Image parallax is delegated to the shared
 * parallax component, which honors prefers-reduced-motion and gates its scroll
 * math behind an IntersectionObserver.
 *
 * @see src/js/front/components/parallax.js
 */

import { initImageParallax } from '../../js/front/components/parallax';

const PLAYING_CLASS = 'is-playing';
const LOADING_CLASS = 'is-loading';
const PAUSED_CLASS = 'is-paused';

/**
 * Whether the visitor asked the OS to minimise motion.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Wire up the ambient background video: autoplay, loader and pause/resume.
 *
 * @param {HTMLElement} block The block root element.
 */
const initVideo = ( block ) => {

	const video = block.querySelector( '.b-intro-star-hero__video' );
	const media = block.querySelector( '.b-intro-star-hero__media' );

	if ( ! video || ! media ) {

		return;

	}

	const toggle = block.querySelector( '.b-intro-star-hero__toggle' );

	// Tracks an explicit user pause so the viewport observer never resumes
	// against the user's intent.
	let userPaused = false;

	/**
	 * Reveal the video once it is actually playing and drop the loader.
	 */
	const onPlaying = () => {

		media.classList.remove( LOADING_CLASS );
		media.classList.add( PLAYING_CLASS );
		media.classList.remove( PAUSED_CLASS );

	};

	/**
	 * Start playback, showing the loader until the first frame renders.
	 */
	const play = () => {

		media.classList.remove( PAUSED_CLASS );

		if ( ! media.classList.contains( PLAYING_CLASS ) ) {

			media.classList.add( LOADING_CLASS );

		}

		const promise = video.play();

		if ( promise && typeof promise.catch === 'function' ) {

			promise.catch( () => media.classList.remove( LOADING_CLASS ) );

		}

	};

	/**
	 * Pause playback and surface the play control.
	 */
	const pause = () => {

		video.pause();
		media.classList.remove( LOADING_CLASS );
		media.classList.add( PAUSED_CLASS );

	};

	video.addEventListener( 'playing', onPlaying );

	if ( toggle ) {

		toggle.addEventListener( 'click', () => {

			if ( video.paused ) {

				userPaused = false;
				play();

			} else {

				userPaused = true;
				pause();

			}

			toggle.setAttribute(
				'aria-label',
				video.paused ? 'Play background video' : 'Pause background video',
			);

		} );

	}

	// Reduced motion: never autoplay — leave the poster and offer the control.
	if ( prefersReducedMotion() ) {

		media.classList.add( PAUSED_CLASS );
		userPaused = true;

	} else if ( 'IntersectionObserver' in window ) {

		// Only play while the hero is on screen; spare the decoder otherwise.
		const observer = new IntersectionObserver(
			( entries ) => {

				entries.forEach( ( entry ) => {

					if ( entry.isIntersecting ) {

						if ( ! userPaused ) {

							play();

						}

					} else if ( ! video.paused ) {

						video.pause();

					}

				} );

			},
			{ threshold: 0.1 },
		);

		observer.observe( block );

	} else {

		play();

	}

};

document.querySelectorAll( '.b-intro-star-hero' ).forEach( ( block ) => {

	if ( block.classList.contains( 'is-video' ) ) {

		initVideo( block );

	}

	if ( block.classList.contains( 'has-parallax' ) ) {

		initImageParallax( block, { imageSelector: '.b-intro-star-hero__image' } );

	}

} );
